import {isArray, isString, isUndefined, noop} from 'lodash';
import React, {memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {Observable} from 'rxjs';
import styled from 'styled-components';

import {findGlobalOrigin} from '../../../libs/shared/helpers/src/browser/browserHelpers';
import {annotateLegacyFrameVisibilityChange} from '../../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {ErrorWithData} from '../../../libs/shared/helpers/src/types/customErrors';
import {isPromise} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {Layer} from '../../../libs/shared/ui/src/components/layers/layer';
import {Loader} from '../../../libs/shared/ui/src/components/loaders/loader';
import {center} from '../../../libs/shared/ui/src/styles/mixins';
import {usePrevious} from '../../../srcCommon/helpers/hookHelpers';
import {usePush, usePushEvents} from '../../../srcCommon/push/pushContext';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';
import {logger} from '../../../srcCommon/services/logger/logger';
import {readApiModel} from '../../helpers/apiHelpers';
import {isLegacyPath} from '../../helpers/legacyHelpers';
import {useAppDispatch} from '../../helpers/reduxHelpers';
import {
  ApiLegacyFrameMethods,
  LegacyFrameMethodsEnum,
  LegacyFrameMethodsModel,
} from '../../models/frames/legacyFrameMethodsModel';
import {SettingsBannersHost} from '../../settings/banners/settingsBannersHost';
import {SettingsBannersProvider} from '../../settings/banners/settingsBannersProvider';
import {SettingsMenuContainer} from '../../settings/menu/settingsMenuContainer';
import {SettingsRouteFinder} from '../../settings/settingsRoutes';
import {SettingsTopbar} from '../../settings/topbar/settingsTopbar';
import {ThunkAction, ThunkAsyncAction} from '../../store/appThunk';
import {AppPanelId, makeSettingsPanelId} from '../../store/panels/panelStateBase';
import {AppFrame} from '../app/appFrame';
import {Iframe} from '../common/iframe';
import {useLinkNavigation} from '../common/navigation/useLinkNavigation';
import {LegacyAppWindow} from './legacyApp';
import {
  composeLegacyFrameAction,
  navigateLegacyFrameAction,
  openUrlLegacyFrameAction,
  resetOfflineCacheLegacyFrameAction,
} from './legacyFrameActions';
import {LegacyFrameRawBoot, toRawBootForLegacyFrame} from './legacyFrameHelpers';
import {SettingsLegacyRewriteRouteFinder} from './legacyRewriteRoutes';

/*
 * Constants.
 */

interface CallLegacyMessage {
  type: string;
  uid?: string;
  method: string;
  args: ReadonlyArray<unknown>;
}

const messageTypeProxy = 'proxy-call';
const legacyPushEventNames = new Set([
  'account_failed',
  'account_saved',
  'analytics_metrics_created',
  'app_notification_received',
  'canned_answer_deleted',
  'canned_answer_updated',
  'canned_answers_updated_batch',
  'canned_answer_folder_created',
  'canned_answer_folder_updated',
  'canned_answer_folder_deleted',
  'canned_answer_folders_updated_batch',
  'card_deleted',
  'card_group_deleted',
  'card_group_updated',
  'card_updated',
  'channel_credentials_failed',
  'channel_credentials_saved',
  'channel_deleted',
  'channel_updated',
  'check_smtp_dns_result',
  'check_smtp_result_completed',
  'check_smtp_result_completed',
  'company_plan_changed',
  'company_status_changed',
  'company_updated',
  'contact_updated',
  'custom_field_deleted',
  'custom_field_updated',
  'export_updated',
  'global_alert',
  'inbox_deleted',
  'inbox_updated',
  'integration_deleted',
  'integration_teammate_settings_updated',
  'integration_test_completed',
  'integration_updated',
  'job_updated',
  'notif_settings_updated',
  'notification_rules_updated',
  'notification_updated',
  'office365_import_saved',
  'plugin_deleted',
  'plugin_updated',
  'role_deleted',
  'role_updated',
  'rule_deleted',
  'rule_updated',
  'sequence_recipient_updated',
  'signature_deleted',
  'signature_updated',
  'tag_id_deleted',
  'tag_updated',
  'team_deleted',
  'team_updated',
  'teammate_changed_update',
  'teammate_deleted',
  'teammate_inboxes_updated',
  'teammate_invited_update_inboxes',
  'teammate_updated',
  'test_pusher',
  'vacation_responder_deleted',
  'vacation_responder_updated',
  'validate_dns_result',
  'validate_smtp_address_result',
]);

/*
 * Props.
 */

interface LegacyFrameProps {
  panelId: AppPanelId;
  rawBoot$: Observable<LegacyFrameRawBoot>;
  isVisible: boolean;
  legacyPath: string | undefined;
  companyUrl: string;
  findNewLegacyRoute: SettingsRouteFinder;
  findRewriteLegacyRoute: SettingsLegacyRewriteRouteFinder;
  hasLazyTeammates: boolean;
}

/*
 * Style.
 */

const StyledAppFrame = styled(AppFrame)`
  display: ${(p) => (p.isVisible ? 'grid' : 'none')};
  grid-template-areas:
    'menu topbar'
    'menu main';
  grid-template-columns: auto 1fr;
  grid-template-rows: auto 1fr;
`;

const StyledSettingsMenu = styled(SettingsMenuContainer)`
  grid-area: menu;
`;

const StyledSettingsTopbar = styled(SettingsTopbar)`
  grid-area: topbar;
`;

const StyledMainDiv = styled.div`
  grid-area: main;
  position: relative;
  overflow: hidden;
`;

const StyledLoaderDiv = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  ${center()};
`;

/*
 * Component.
 */

export const LegacyFrame = memo<LegacyFrameProps>((props) => {
  const dispatch = useAppDispatch();
  const {
    panelId,
    rawBoot$,
    isVisible,
    legacyPath,
    children,
    companyUrl,
    findNewLegacyRoute,
    findRewriteLegacyRoute,
    hasLazyTeammates,
  } = props;
  const settingsPanelId = makeSettingsPanelId(panelId);
  const {navigateToLink} = useLinkNavigation();

  // The Old Front path or the Settings path to be rendered, if any.
  const visibleLegacyPath = isVisible ? legacyPath : undefined;
  const settingsPath = visibleLegacyPath?.startsWith('/settings') ? visibleLegacyPath : undefined;

  // The route to be served by New Front. In practice, this is a Settings route that has been migrated to New Front.
  const newLegacyRoute = useMemo(
    () => findNewLegacyRoute(visibleLegacyPath),
    [findNewLegacyRoute, visibleLegacyPath],
  );

  // The path to be served by Old Front.
  const oldLegacyPath = !isUndefined(visibleLegacyPath) && !newLegacyRoute ? visibleLegacyPath : '/noop';

  const [isBooted, setIsBooted] = useState(false);
  const [hasOverlay, setHasOverlay] = useState(false);

  const pendingRawBootsRef = useRef<ReadonlyArray<unknown>>([]);
  const frameWindowRef = useRef<LegacyAppWindow>();
  const isNavigatingLegacyAppRef = useRef(false);
  const isLoadedRef = useRef(false);
  const hasGoneOnlineRef = useRef<boolean>(false);
  const origin = useMemo(() => findGlobalOrigin(), []);

  const {isConnected} = usePush();

  // Load the frame only when online.
  if (isConnected) {
    hasGoneOnlineRef.current = true;
  }

  // Check if we should route the user to a new path.
  const legacyPathReRouted = useMemo(
    () => findRewriteLegacyRoute(visibleLegacyPath),
    [findRewriteLegacyRoute, visibleLegacyPath],
  );
  const previousLegacyPathReRouted = usePrevious(legacyPathReRouted);

  useEffect(() => {
    // If we do not have a re-routed path or we already tried to re-route the user.
    if (!legacyPathReRouted || legacyPathReRouted === previousLegacyPathReRouted) {
      return;
    }
    navigateToLink(legacyPathReRouted);
  }, [legacyPathReRouted, navigateToLink, previousLegacyPathReRouted]);

  /*
   * Send updates to the frame.
   */

  const callIframeMethod = useCallback(
    (method: string, ...args: unknown[]) => {
      const currentFrameWindow = frameWindowRef.current;
      if (!currentFrameWindow) {
        return;
      }

      const message: CallLegacyMessage = {type: messageTypeProxy, method, args};
      currentFrameWindow.postMessage(message, origin);
    },
    [origin],
  );

  const sendIframeBoot = useCallback(
    (rawBoot: unknown) => {
      callIframeMethod('boot', rawBoot);
      setIsBooted(true);
    },
    [callIframeMethod],
  );

  const navigateLegacyAppToUrl = useCallback((url: string): void => {
    const frameWindow = frameWindowRef.current;
    if (!frameWindow) {
      return;
    }

    // To help guard against an infinite loop/recursion.
    isNavigatingLegacyAppRef.current = true;
    try {
      frameWindow.frontLegacyApp.navigateToUrl(url);
    } finally {
      isNavigatingLegacyAppRef.current = false;
    }
  }, []);

  /*
   * Event handlers.
   */

  const onRawBoot: HandlerOf<unknown> = useCallback(
    (rawBoot) => {
      // If the frame is loaded, just pass it on.
      if (isLoadedRef.current) {
        if (!(rawBoot as any)?.user || !(rawBoot as any)?.company) {
          reportToBugsnag(new Error('Sending invalid raw boot to old front.'));
        }
        sendIframeBoot(rawBoot);
        return;
      }

      // Otherwise, hold on to it until we do load the frame.
      pendingRawBootsRef.current = [...pendingRawBootsRef.current, rawBoot];
    },
    [sendIframeBoot],
  );

  const onFrameLoaded = useCallback(
    ({source}: MessageEvent) => {
      if (!source) {
        return;
      }

      const frameWindow = source as LegacyAppWindow;
      frameWindowRef.current = frameWindow;
      isLoadedRef.current = true;

      // If we are holding on to a raw boot blob, pass it to the frame and release it.
      const rawBoots = pendingRawBootsRef.current;
      pendingRawBootsRef.current = [];
      rawBoots.forEach((rawBoot) => sendIframeBoot(rawBoot));

      const {frontLegacyApp} = frameWindow;

      frontLegacyApp.hideSettingsChrome();
      frontLegacyApp.hideDuplicateElementsFromP0();

      frontLegacyApp.registerRewriteDelegate({
        navigateToUrl: (url) => {
          // Ignore Old Front's no-op URL.
          if (url === '/noop') {
            return false;
          }

          // Guard against an infinite loop/recursion.
          const isNavigatingLegacyApp = isNavigatingLegacyAppRef.current;
          if (isNavigatingLegacyApp) {
            return false;
          }

          // Handle the URL. At a minimum, this will update our window history.
          dispatch(navigateLegacyFrameAction(panelId, url));

          // Let Old Front handle the URL if it's a "legacy" path and it hasn't been migrated to New Front.
          const isLegacyAppUrl = isLegacyPath(url) && !findNewLegacyRoute(url);
          return !isLegacyAppUrl;
        },
        setHasOverlay,
      });
    },
    [panelId, sendIframeBoot, dispatch, findNewLegacyRoute],
  );

  const onFrameMessage = useCallback(
    async (event: MessageEvent) => {
      // Validate the origin of the event/
      if (event.origin !== origin || !event.data || event.data.type !== messageTypeProxy) {
        return;
      }

      const {method: methodName, args: methodArguments} = event.data;
      if (!isString(methodName) || !isArray(methodArguments)) {
        return;
      }

      // Parse our input to ensure correct types.
      const rawLegacyFrameMethod = {[methodName]: methodArguments.length > 0 ? methodArguments : undefined};
      const legacyFrameMethod = readApiModel(ApiLegacyFrameMethods, rawLegacyFrameMethod);

      // Handle the initial "loaded" event.
      if (methodName === LegacyFrameMethodsEnum.LOADED) {
        onFrameLoaded(event);
        return;
      }

      // For any other proxy call, we need to be loaded first.
      if (!isVisible || !isLoadedRef.current || !isLegacyFrameMethodName(methodName, legacyFrameMethod)) {
        return;
      }

      const method = legacyFrameMethods[methodName];
      if (!method) {
        throw new ErrorWithData('Legacy frame method not found.', {methodName, methodArguments});
      }

      // Run the method and get the action, if any.
      const methodAction = method(...((legacyFrameMethod[methodName] as any) || []));
      if (!methodAction) {
        return;
      }

      // If one was found, dispatch it.
      const methodActionResult = dispatch(methodAction);

      // If this is not a promise, stop here.
      if (!isPromise(methodActionResult)) {
        return;
      }

      // Otherwise, wait for the promise to complete.
      await methodActionResult;
    },
    [origin, isVisible, dispatch, onFrameLoaded],
  );

  // Send the boot.
  useEffect(() => {
    const subscription = rawBoot$
      .pipe(toRawBootForLegacyFrame(companyUrl, hasLazyTeammates))
      .subscribe(onRawBoot);
    return () => subscription.unsubscribe();
  }, [rawBoot$, onRawBoot, companyUrl, hasLazyTeammates]);

  // Send location.
  useLayoutEffect(() => {
    if (!isBooted) {
      return;
    }

    try {
      navigateLegacyAppToUrl(oldLegacyPath);
    } catch (error) {
      logger.error('Unable to navigate to legacy url.', {
        path: oldLegacyPath,
        message: `${error?.message}`,
      });
    }
  }, [isBooted, navigateLegacyAppToUrl, oldLegacyPath]);

  // Send pusher events.
  usePushEvents(
    (eventName, eventData) => {
      if (!isLoadedRef.current || !legacyPushEventNames.has(eventName)) {
        return;
      }

      callIframeMethod('dispatchPushEvent', eventName, eventData);
    },
    [callIframeMethod],
  );

  const maybeExitSettings = (onDiscard: Handler) => {
    const frameWindow = frameWindowRef.current;
    if (!frameWindow) {
      onDiscard();
      return;
    }

    frameWindow.frontLegacyApp.maybeExitSettings(onDiscard);
  };

  useEffect(() => {
    annotateLegacyFrameVisibilityChange(isVisible);
  }, [isVisible]);

  /*
   * Render.
   */

  return (
    <>
      <Layer>{children}</Layer>
      <Layer>
        <StyledAppFrame isVisible={isVisible}>
          {!isUndefined(settingsPath) && (
            <>
              <StyledSettingsMenu
                settingsPath={settingsPath}
                maybeExitSettings={maybeExitSettings}
                hasOverlay={hasOverlay && !newLegacyRoute}
              />
              <StyledSettingsTopbar settingsPath={settingsPath} hasOverlay={hasOverlay && !newLegacyRoute} />
            </>
          )}
          <StyledMainDiv>
            <SettingsBannersProvider>
              {!isUndefined(settingsPath) && <SettingsBannersHost />}
              {newLegacyRoute?.route.render(settingsPanelId, newLegacyRoute.match)}
              {hasGoneOnlineRef.current ? (
                <Iframe
                  endpoint="/?serve=legacy"
                  title="Settings"
                  isVisible={!newLegacyRoute && isBooted}
                  onMessage={onFrameMessage}
                />
              ) : null}
              {!newLegacyRoute && !isBooted && (
                <StyledLoaderDiv>
                  <Loader />
                </StyledLoaderDiv>
              )}
            </SettingsBannersProvider>
          </StyledMainDiv>
        </StyledAppFrame>
      </Layer>
    </>
  );
});

/*
 * Helpers.
 */

function isLegacyFrameMethodName(
  methodName: string,
  legacyFrameMethod: LegacyFrameMethodsModel,
): methodName is keyof LegacyFrameMethodsModel {
  return methodName in legacyFrameMethod;
}

/*
 * Legacy frame method mapping.
 */

type LegacyFrameMethodReturnValue = void | ThunkAction<unknown> | ThunkAsyncAction<unknown>;
type LegacyFrameMethod<
  T extends keyof LegacyFrameMethodsModel,
  M extends LegacyFrameMethodsModel[T],
> = M extends any[] ? (...args: M) => LegacyFrameMethodReturnValue : () => LegacyFrameMethodReturnValue;
type LegacyFrameMethods = {
  [T in keyof LegacyFrameMethodsModel]?: LegacyFrameMethod<T, LegacyFrameMethodsModel[T]>;
};

const legacyFrameMethods: LegacyFrameMethods = {
  [LegacyFrameMethodsEnum.COMPOSE]: composeLegacyFrameAction,
  [LegacyFrameMethodsEnum.NAVIGATE]: noop,
  [LegacyFrameMethodsEnum.OPEN_URL]: openUrlLegacyFrameAction,
  [LegacyFrameMethodsEnum.RESET_OFFLINE_CACHE]: resetOfflineCacheLegacyFrameAction,
};
